import React, { useState, useCallback } from 'react';
import { styles } from '../../../utils/styles';
import { RemoveIcon } from 'assests/GmsIcons';
import { useIconUpload } from '../../../hooks/useIconUpload';
import { useDebouncedInput } from '../../../hooks/useDebouncedInput';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';

type HeaderConfigType = {
    showHeader: boolean;
    showTitle: boolean;
    titleText: string;
    titleLayout: 'Left, Below Logo' | 'Centre';
    logoUrl: string;
    logoFile?: File | null;
    showResetButton: boolean;
    resetButtonText: string;
    resetButtonShowIcon: boolean;
    resetButtonIconUrl?: string;
    resetButtonIconFile?: File | null;
    resetButtonShowTooltip: boolean;
    resetButtonTooltipText?: string;
    showCloseButton: boolean;
    closeButtonText: string;
    closeButtonShowIcon: boolean;
    closeButtonIconUrl?: string;
    closeButtonIconFile?: File | null;
    closeButtonShowTooltip: boolean;
    closeButtonTooltipText?: string;
};

type HeaderConfigProps = {
    id?: string;
    initialConfig?: Partial<HeaderConfigType>;
    onConfigChange?: (config: HeaderConfigType & {
        logoFile?: File | null;
        resetButtonIconFile?: File | null;
        closeButtonIconFile?: File | null;
    }) => void;
}

export const HeaderConfig = ({ id, initialConfig, onConfigChange }: HeaderConfigProps) => {
    const baseId = id || 'header-config';
    const defaultConfig: HeaderConfigType = {
        showHeader: true,
        showTitle: true,
        titleText: 'VFD Builder',
        titleLayout: 'Centre',
        logoUrl: '',
        showResetButton: true,
        resetButtonText: 'Reset',
        resetButtonShowIcon: false,
        resetButtonIconUrl: '',
        resetButtonShowTooltip: false,
        resetButtonTooltipText: '',
        showCloseButton: true,
        closeButtonText: 'Close',
        closeButtonShowIcon: false,
        closeButtonIconUrl: '',
        closeButtonShowTooltip: false,
        closeButtonTooltipText: ''
    };

    const [config, setConfig] = useState<HeaderConfigType>({
        ...defaultConfig,
        ...initialConfig
    });

    // Use custom hooks for icon management
    const logoUpload = useIconUpload(
        initialConfig?.logoUrl || '',
        (url) => updateConfig({ logoUrl: url })
    );

    const resetIconUpload = useIconUpload(
        initialConfig?.resetButtonIconUrl || '',
        (url) => updateConfig({ resetButtonIconUrl: url })
    );

    const closeIconUpload = useIconUpload(
        initialConfig?.closeButtonIconUrl || '',
        (url) => updateConfig({ closeButtonIconUrl: url })
    );

    // Use custom hooks for debounced text inputs
    const titleInput = useDebouncedInput(
        config.titleText,
        (value) => updateConfig({ titleText: value })
    );

    const resetButtonTextInput = useDebouncedInput(
        config.resetButtonText,
        (value) => updateConfig({ resetButtonText: value })
    );

    const closeButtonTextInput = useDebouncedInput(
        config.closeButtonText,
        (value) => updateConfig({ closeButtonText: value })
    );

    const resetTooltipInput = useDebouncedInput(
        config.resetButtonTooltipText || '',
        (value) => updateConfig({ resetButtonTooltipText: value })
    );

    const closeTooltipInput = useDebouncedInput(
        config.closeButtonTooltipText || '',
        (value) => updateConfig({ closeButtonTooltipText: value })
    );

    const updateConfig = useCallback((updates: Partial<HeaderConfigType>) => {
        setConfig(prevConfig => {
            const newConfig = { ...prevConfig, ...updates };
            setTimeout(() => {
                if (onConfigChange) {
                    onConfigChange({
                        ...newConfig,
                        logoFile: logoUpload.file,
                        resetButtonIconFile: resetIconUpload.file,
                        closeButtonIconFile: closeIconUpload.file
                    });
                }
            }, 0);
            return newConfig;
        });
    }, [onConfigChange, logoUpload.file, resetIconUpload.file, closeIconUpload.file]);

    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pb-2 tw-my-4 tw-border-b tw-border-[#D1D1D6]">
                <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Header</h2>
                <ToggleButton
                    id={`${baseId}-show-header-toggle`}
                    name="show-header"
                    value={config.showHeader}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateConfig({ showHeader: e.target.checked })}
                />
            </div>

            <div className={styles.section}>
                <h3 className={styles.subSectionTitle}>Logo</h3>

                {logoUpload.previewUrl && (
                    <div className="tw-mb-2">
                        <div className="tw-relative tw-inline-block">
                            <img
                                src={logoUpload.previewUrl}
                                alt="Logo preview"
                                className="tw-max-w-[150px] tw-max-h-[80px] tw-border tw-border-gray-300 tw-rounded tw-p-2"
                            />
                            <button
                                onClick={logoUpload.handleRemove}
                                className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-text-sm hover:tw-bg-red-600"
                                title="Remove logo"
                            >
                                <RemoveIcon />
                            </button>
                        </div>
                    </div>
                )}

                <input
                    ref={logoUpload.inputRef}
                    type="file"
                    accept="image/*"
                    onChange={logoUpload.handleUpload}
                    className="tw-hidden"
                    id="logo-upload-input"
                />

                <button
                    className={styles.uploadButton}
                    onClick={() => logoUpload.inputRef.current?.click()}
                >
                    {logoUpload.previewUrl ? 'Change Logo Image' : 'Upload Logo Image'}
                </button>
            </div>
            
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                <input
                    type="checkbox"
                    id="showTitle"
                    checked={config.showTitle}
                    onChange={(e) => updateConfig({ showTitle: e.target.checked })}
                    className={styles.checkboxInput}
                />
                <label htmlFor="showTitle" className={styles.checkboxLabel}>{`Show 'Title'`}</label>
                </div>
                
                {config.showTitle && (
                <div className={styles.nestedContainer}>
                    <div className={styles.nestedInputContainer}>
                    <label className={styles.inputLabel}>Change Title Name</label>
                    <input
                        type="text"
                        value={titleInput.localValue}
                        onChange={titleInput.handleChange}
                        className={styles.input}
                    />
                    </div>
                    
                    <div className={styles.nestedInputContainer}>
                    <label className={styles.inputLabel}>Title Layout</label>
                    <div className={styles.selectContainer}>
                        <select
                        value={config.titleLayout}
                        onChange={(e) => updateConfig({ 
                            titleLayout: e.target.value as 'Left, Below Logo' | 'Centre' 
                        })}
                        className={styles.select}
                        >
                        <option value="Left">Left, Below Logo</option>
                        <option value="Centre">Centre</option>
                        </select>
                        <div className={styles.selectIcon}>
                        <svg className="tw-w-4 tw-h-4 tw-text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                        </svg>
                        </div>
                    </div>
                    </div>
                </div>
                )}
            </div>
            
            {/* Rest of the component configuration remains the same */}
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                <input
                    type="checkbox"
                    id="showResetButton"
                    checked={config.showResetButton}
                    onChange={(e) => updateConfig({ showResetButton: e.target.checked })}
                    className={styles.checkboxInput}
                />
                <label htmlFor="showResetButton" className={styles.checkboxLabel}>{`Show 'Reset' Button`}</label>
                </div>
                
                {config.showResetButton && (
                <div className={styles.nestedContainer}>
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showResetText"
                        checked={true} 
                        className={styles.checkboxInput}
                        readOnly
                    />
                    <label htmlFor="showResetText" className={styles.checkboxLabelSmall}>Show Text</label>
                    </div>
                    
                    <div className={styles.nestedInputContainerWithMargin}>
                    <label className={styles.inputLabel}>Button Label</label>
                    <input
                        type="text"
                        value={resetButtonTextInput.localValue}
                        onChange={resetButtonTextInput.handleChange}
                        className={styles.input}
                    />
                    </div>
                    
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showResetIcon"
                        checked={config.resetButtonShowIcon}
                        onChange={(e) => updateConfig({ resetButtonShowIcon: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showResetIcon" className={styles.checkboxLabelSmall}>Show Icon</label>
                    </div>
                    
                    {config.resetButtonShowIcon && (
                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Reset Button Icon</label>

                            {resetIconUpload.previewUrl && (
                                <div className="tw-mb-2">
                                    <div className="tw-relative tw-inline-block">
                                        <img
                                            src={resetIconUpload.previewUrl}
                                            alt="Reset icon preview"
                                            className="tw-max-w-[40px] tw-max-h-[40px] tw-border tw-border-gray-300 tw-rounded tw-p-1"
                                        />
                                        <button
                                            onClick={resetIconUpload.handleRemove}
                                            className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-xs hover:tw-bg-red-600"
                                            title="Remove icon"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}

                            <input
                                ref={resetIconUpload.inputRef}
                                type="file"
                                accept="image/*"
                                onChange={resetIconUpload.handleUpload}
                                className="tw-hidden"
                                id="reset-icon-upload-input"
                            />

                            <button
                                className={styles.uploadButton}
                                onClick={() => resetIconUpload.inputRef.current?.click()}
                            >
                                {resetIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon'}
                            </button>

                            {resetIconUpload.file && (
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Selected: {resetIconUpload.file.name}
                                </p>
                            )}
                        </div>
                    )}
                    
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showResetTooltip"
                        checked={config.resetButtonShowTooltip}
                        onChange={(e) => updateConfig({ resetButtonShowTooltip: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showResetTooltip" className={styles.checkboxLabelSmall}>Show tool tip on mouse hover</label>
                    </div>
                    
                    {/* Tooltip text input for Reset button */}
                    {config.resetButtonShowTooltip && (
                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Text for tool tip</label>
                            <input
                                type="text"
                                value={resetTooltipInput.localValue}
                                onChange={resetTooltipInput.handleChange}
                                placeholder="Enter"
                                className={styles.input}
                            />
                        </div>
                    )}
                </div>
                )}
            </div>
            
            <div className={styles.section}>
                <div className={styles.checkboxContainer}>
                <input
                    type="checkbox"
                    id="showCloseButton"
                    checked={config.showCloseButton}
                    onChange={(e) => updateConfig({ showCloseButton: e.target.checked })}
                    className={styles.checkboxInput}
                />
                <label htmlFor="showCloseButton" className={styles.checkboxLabel}>{`Show 'Close' Button`}</label>
                </div>
                
                {config.showCloseButton && (
                <div className={styles.nestedContainer}>
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showCloseText"
                        checked={true} 
                        className={styles.checkboxInput}
                        readOnly
                    />
                    <label htmlFor="showCloseText" className={styles.checkboxLabelSmall}>Show Text</label>
                    </div>
                    
                    <div className={styles.nestedInputContainerWithMargin}>
                    <label className={styles.inputLabel}>Button Label</label>
                    <input
                        type="text"
                        value={closeButtonTextInput.localValue}
                        onChange={closeButtonTextInput.handleChange}
                        className={styles.input}
                    />
                    </div>
                    
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showCloseIcon"
                        checked={config.closeButtonShowIcon}
                        onChange={(e) => updateConfig({ closeButtonShowIcon: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showCloseIcon" className={styles.checkboxLabelSmall}>Show Icon</label>
                    </div>
                    
                    {config.closeButtonShowIcon && (
                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Close Button Icon</label>

                            {closeIconUpload.previewUrl && (
                                <div className="tw-mb-2">
                                    <div className="tw-relative tw-inline-block">
                                        <img
                                            src={closeIconUpload.previewUrl}
                                            alt="Close icon preview"
                                            className="tw-max-w-[40px] tw-max-h-[40px] tw-border tw-border-gray-300 tw-rounded tw-p-1"
                                        />
                                        <button
                                            onClick={closeIconUpload.handleRemove}
                                            className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-xs hover:tw-bg-red-600"
                                            title="Remove icon"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}

                            <input
                                ref={closeIconUpload.inputRef}
                                type="file"
                                accept="image/*"
                                onChange={closeIconUpload.handleUpload}
                                className="tw-hidden"
                                id="close-icon-upload-input"
                            />

                            <button
                                className={styles.uploadButton}
                                onClick={() => closeIconUpload.inputRef.current?.click()}
                            >
                                {closeIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon'}
                            </button>

                            {closeIconUpload.file && (
                                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    Selected: {closeIconUpload.file.name}
                                </p>
                            )}
                        </div>
                    )}
                    
                    <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        id="showCloseTooltip"
                        checked={config.closeButtonShowTooltip}
                        onChange={(e) => updateConfig({ closeButtonShowTooltip: e.target.checked })}
                        className={styles.checkboxInput}
                    />
                    <label htmlFor="showCloseTooltip" className={styles.checkboxLabelSmall}>Show tool tip on mouse hover</label>
                    </div>
                    
                    {/* Tooltip text input for Close button */}
                    {config.closeButtonShowTooltip && (
                        <div className={styles.nestedInputContainerWithMargin}>
                            <label className={styles.inputLabel}>Text for tool tip</label>
                            <input
                                type="text"
                                value={closeTooltipInput.localValue}
                                onChange={closeTooltipInput.handleChange}
                                placeholder="Enter"
                                className={styles.input}
                            />
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};